import { getTenantUnitOfMeasure } from './tenantUtils';
import APPLICATION_CONSTANTS from '../applicationConstants';
import { isType } from './typeUtils';
import { Types } from '../enums';
import Session from '../session';
import { SessionStorageKeys } from '../constants';
import { getDefaultSearchDistanceRadius } from './searchFilterUtils';

/**
 * Generates the payload to send to the search API.
 * @param {Object} config The tenant configuration.
 * @param {Object} coords The latitude/longitude coordinates.
 * @param {String} address The location address.
 * @returns Object
 */
export function generateUrlPayload(config, coords, address, locationProps) {
  const skipRedirect = shouldSkipRedirect() || APPLICATION_CONSTANTS.GLOBAL.BEHAVIOR === 'local-search';
  const { search, hash } = locationProps;
  const queryStringValues = parseQueryStrings(search, hash);
  const payload = {
    lat: coords?.latitude,
    long: coords?.longitude,
    address: address,
    skipRedirect: skipRedirect,
    miles: getDefaultSearchDistanceRadius(config),
    limit: config.results_per_page,
    lang: APPLICATION_CONSTANTS.GLOBAL.CURRENT_LANGUAGE,
    currentPage: process.env.LOCATOR_SEARCH_FIRST_PAGE,
    unitOfMeasurement: getTenantUnitOfMeasure(config),
    userId: queryStringValues?.userId,
    locale: queryStringValues?.locale,
    storeId: queryStringValues?.storeId
  };

  return payload;
}

/**
 * Formats the filtered values in array form and object form.
 * @param {Object} filteredValues The filtered values to format.
 * @returns Array
 */
export function formatFilteredValues(filteredValues) {
  const formattedArrayFilteredValues = [];
  const formattedObjectFilteredValues = {};

  if (filteredValues) {
    for (const [key, value] of Object.entries(filteredValues)) {
      let filterArrayValues = [];

      if (isType(value, Types.array)) {
        filterArrayValues = value;
        formattedObjectFilteredValues[key] = filteredValues[key];
      }
      else {
        filterArrayValues = [value];
        formattedObjectFilteredValues[key] = [filteredValues[key]];
      }

      formattedArrayFilteredValues.push({
        key: key,
        values: filterArrayValues
      });
    }
  }

  return [formattedArrayFilteredValues, formattedObjectFilteredValues];
}

/**
 * Parses the filtered values out of the query string and converts them to an object.
 * @param {String} search The query string portion of the url.
 * @returns Object
 */
export function parseFilteredValues(search) {
  const parsedFilteredValues = {};

  if (search) {
    const indexOfFilteredSearch = search.indexOf('&filteredSearch=1');
    if (indexOfFilteredSearch >= 0) {
      const urlSearchParam = new URLSearchParams(search.slice(indexOfFilteredSearch + 17));

      for (const [key, value] of urlSearchParam.entries()) {
        if (key in parsedFilteredValues) {
          parsedFilteredValues[key].push(value);
        } else {
          parsedFilteredValues[key] = [value];
        }
      }
    }
  }
  return parsedFilteredValues;
}

/**
 * Parses the provided query string portion of a url into an object.
 * @param {String} search The query string portion of the url.
 * @param {String} hash (Optional) The string after the hash (#) in the url.
 * @returns Object
 */
export function parseQueryStrings(search, hash) {
  let parsedQueryStrings = {};
  let parsedHashString = {};

  if (search) {
    const urlSearchParam = new URLSearchParams(search);

    for (const [key, value] of urlSearchParam.entries()) {
      parsedQueryStrings[key] = value;
    }

    // for forced filtered search. ensures location is at beginning of the query.
    const keysToMove = ['address', 'lat', 'long'];
    parsedQueryStrings = Object.assign(
      {},
      ...keysToMove.map((k) => ({ [k]: parsedQueryStrings[k] })),
      parsedQueryStrings
    );

    if (hash) {
      const urlHashString = new URLSearchParams(hash);
      
      // create a new object from the hash string
      for (const [key, value] of urlHashString.entries()) {
        parsedHashString[key] = value;
      }
  
      // assume that the first object key is the second half of the address string.
      const firstObjectKey = Object.keys(parsedHashString)[0];
  
      // remove prop from object before merging with parsed query string
      delete parsedHashString[firstObjectKey];

      // merge parsed objects and replace address prop with the full address
      return {
        ...parsedQueryStrings,
        ...parsedHashString,
        address: `${parsedQueryStrings.address}${firstObjectKey}`
      };
    }
  }

  return parsedQueryStrings;
}

/**
 * Removes the search filtered portion of the query strings.
 * @param {String} search The query string portion of the url.
 * @returns String
 */
export function removeFilteredQueryStrings(search) {
  let queryStringsWithoutFilters = search;

  if (search) {
    const indexOfFilteredSearch = search.indexOf('&filteredSearch=1');

    if (indexOfFilteredSearch >= 0) {
      queryStringsWithoutFilters = search.slice(0, indexOfFilteredSearch);
    }
  }

  return queryStringsWithoutFilters;
}

/**
 * Determines whether the application should skip redirecting the user to the results page.
 * @returns Boolean
 */
export function shouldSkipRedirect() {
  const { skipRedirect } = parseQueryStrings(window.location.href);
  const botsList = '(googlebot|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)';
  const botRegex = new RegExp(botsList, 'i');
  const isBot = botRegex.test(navigator.userAgent);

  return skipRedirect || window.locatorTenancy.skipRedirect || isBot;
}

/**
 * Converts the provided object query string values to an equivalent query string.
 * @param {Object} queryStringValues 
 * @returns String
 */
export function stringifyQueryStrings(queryStringValues) {
  let queryStrings = '';

  if (queryStringValues) {
    for (const [key, value] of Object.entries(queryStringValues)) {
      if (value) {
        if(key == 'Distance') {
          queryStrings += `${key}=${value}&`;
        } 
        else if (Array.isArray(value)) {
          for(const index in value) {
            queryStrings += `${key}=${value[index].includes('&') ? value[index].replace('&','%26') : value[index]}&`;
          }
        }
        else {        
          queryStrings += `${key}=${value}&`;
        }
      }
    }

    // Remove the trailing &.
    queryStrings = queryStrings.slice(0, -1);
  }

  return queryStrings;
}

export function setPracticeGroup(config, practiceGroup = null) {
  const { remove_city_practice_group_on_search } = config;
  const isSubsequentSearch = Session.getItem(SessionStorageKeys.isSubsequentSearch);
  const shouldRemovePracticeGroup =  !practiceGroup
    || (isSubsequentSearch && practiceGroup.startsWith('ct') && remove_city_practice_group_on_search);

  if (shouldRemovePracticeGroup) {
    return null;
  }

  return practiceGroup;
}
